import React, { useState, useEffect, useCallback } from "react";
import styled, { css } from "styled-components";
import { Link, useLocation } from "react-router-dom";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faBars } from "@fortawesome/free-solid-svg-icons";
import lightLogo from "assets/images/light-logo.svg";
import darkLogo from "assets/images/dark-logo.svg";
import Switch from "react-switch";
import { useLayoutContext } from "context/useLayoutContext";
import { Icon } from "@iconify/react";
import animatedScrollTo from "animated-scroll-to";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { toggleTheme, theme } = useLayoutContext();

  const location = useLocation();
  const hash = location.hash;

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 0);
  }, []);

  const handleResize = useCallback(() => {
    if (window.innerWidth > 768 && isOpen) {
      setIsOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, handleResize]);

  const handleScrollTo = (selector) => {
    const element = document.querySelector(selector);
    if (element) {
      const offsetPosition =
        element.getBoundingClientRect().top + window.scrollY - 40;
      animatedScrollTo(offsetPosition);
    }
  };

  useEffect(() => {
    // --| Check if there's a hash in the URL
    const currentLocation = location.hash;
    const selector = currentLocation.slice(1);

    if (currentLocation) {
      // --| If hash is found, scroll to the element it points to
      const element = document.querySelector(`.${selector}`);
      
      if (element) {
        // --| Offset the top because of the header
        const offsetPosition =
          element.getBoundingClientRect().top + window.scrollY - 40;
        animatedScrollTo(offsetPosition);
      } else {
        // --| If incorect hash, scroll to the top of the page
        window.history.replaceState(null, "", location.pathname);
        animatedScrollTo(0);
      }
    } else {
      // --| If no hash, scroll to the top of the page
      animatedScrollTo(0);
    }
  }, [location.hash, location.pathname]);

  return (
    <>
      <NavbarWrapper isScrolled={isScrolled}>
        <NavbarContainer>
          <LogoContainer>
            <Link to="/">
              {theme === "dark" ? (
                <img src={darkLogo} alt="dark-hero" />
              ) : (
                <img src={lightLogo} alt="Corpine Logo" />
              )}
            </Link>
          </LogoContainer>
          <NavLinks>
            <NavLink className="nav-link"
              isActive={hash === ""} onClick={() => animatedScrollTo(0)} href="">Home</NavLink>
            <NavLink
              className="nav-link"
              isActive={hash === "#about"}
              href="#about"
              onClick={() => handleScrollTo(".about")}
            >
              About
            </NavLink>
            <NavLink
              className="nav-link"
              isActive={hash === "#services"}
              href="#services"
              onClick={() => handleScrollTo(".services")}
            >
              Services
            </NavLink>
            <NavLink
              className="nav-link"
              isActive={hash === "#contact"}
              href="#contact"
              onClick={() => handleScrollTo(".contact")}
            >
              Contact
            </NavLink>
          </NavLinks>
          <RightNavLink>
            <SwitchContainer>
              <Switch
                onChange={toggleTheme}
                checked={theme === "dark"}
                checkedIcon={
                  <CheckedIcon icon="mingcute:sun-fill" fontSize={20} />
                }
                uncheckedIcon={
                  <UnCheckedIcon
                    icon="teenyicons:moon-solid"
                    color="#013EBA"
                    fontSize={20}
                  />
                }
                onColor="#1D1D1F"
                offColor="#FBFBFB"
                offHandleColor="#013EBA"
              />
            </SwitchContainer>
            <MobileIcon onClick={toggleMenu}>
            {isOpen ? (
              <Icon icon="ic:round-cancel" />
            ) : (
              <Icon icon="icon-park-outline:hamburger-button" />
            )}
          </MobileIcon>
          </RightNavLink>

        </NavbarContainer>
      </NavbarWrapper>

      {/* Mobile Overlay */}
      <Overlay isOpen={isOpen}>
        {/* <CloseIcon onClick={toggleMenu}>
          <FontAwesomeIcon icon={faBars} />
        </CloseIcon> */}
        <OverlayLinks>
          <NavLink className="nav-link" href="#home" isActive={hash === "#home"} onClick={() => {animatedScrollTo(0); toggleMenu()}}>
            Home
          </NavLink>
          <NavLink className="nav-link" href="#about" isActive={hash === "#about"} onClick={() => {handleScrollTo(".about"); toggleMenu()}}>
            About
          </NavLink>
          <NavLink className="nav-link" href="#services" isActive={hash === "#services"} onClick={() => {handleScrollTo(".services"); toggleMenu()}}>
            Services
          </NavLink>
          <NavLink className="nav-link"href="#contact" isActive={hash === "#contact"} onClick={() => {handleScrollTo(".contact"); toggleMenu()}}>
            Contact
          </NavLink>
        </OverlayLinks>
      </Overlay>
    </>
  );
};

export default Navbar;

const NavbarWrapper = styled.div`
  width: 100%;
  position: fixed;
  top: 0;
  display: flex;
  justify-content: center;
  z-index: 9999;
  background-color: ${({ theme }) => theme.background};

  ${({ isScrolled }) =>
    isScrolled &&
    css`
      box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.3);
    `}
`;

const LogoContainer = styled.div`
  img {
    width: 150px;
  }
  @media (max-width: 576px) {
    z-index: 2;
  }
  @media(max-width:426px) {
    width: 130px;
  }
`;

//--| Main Navbar container
const NavbarContainer = styled.nav`
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ theme }) => theme.color};
  max-width: 100%;
  @media (min-width: 1366px) {
    width: 1300px;
  }
  @media(max-width: 1366px){
    width: 100%;
    padding-left: 30px;
    padding-right: 30px;
    box-sizing: border-box;
  }
`;

const SwitchContainer = styled.div`
  border: 1px solid #cce0ff;
  padding: 3px 8px;
  border-radius: 22px;

  @media(max-width: 426px){
    padding: 1px 3px;
  }
`;

// Links container for desktop view
const NavLinks = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const RightNavLink = styled.div`
  display: flex;
  align-items: center;
`;

// Link item
const NavLink = styled.a`
  color: ${({ theme }) => theme.color};
  text-decoration: none;
  font-size: 1rem;

  ${(props) =>
    props.isActive &&
    css`
      border-bottom: ${({ theme }) => `2px solid ${theme.navlink}`};
    `}

  &:hover {
    color: #aaa;
  }

  @media(max-width: 768px){
    line-height: 30px;
    border-bottom: none;
    ${(props) =>
      props.isActive &&
      css`
        color: #0EDC96;
      `}
    // width: 60px;
    

  }
`;

// Mobile toggle icon
const MobileIcon = styled.div`
  display: none;
  font-size: 1.8rem;
  cursor: pointer;
  color: ${({ theme }) => theme.navlink};

  @media (max-width: 768px) {
    display: block;
    height: 30px;
    padding: 3px 8px;
  }
`;

const CheckedIcon = styled(Icon)`
  margin-top: 5px;
`;

const UnCheckedIcon = styled(Icon)`
  margin-top: 5px;
  margin-left: 12px;
`;

// Mobile menu overlay
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  flex-direction: column;
  transform: ${({ isOpen }) => (isOpen ? "translateX(0)" : "translateX(100%)")};
  transition: transform 0.3s ease-in-out;
  z-index: 9;
`;

// Close icon inside overlay
// const CloseIcon = styled.div`
//   position: absolute;
//   top: 20px;
//   right: 20px;
//   font-size: 2rem;
//   cursor: pointer;
//   color: ${({ theme }) => theme.navlink};
// `;

// Links container in overlay
const OverlayLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  text-align: left;
  // position: abosolute;
  padding-top: 80px;
  padding-left: 30px;

  @media(max-width: 768px){
    padding-top: 100px;
  }
`;
